<script setup lang="ts">
import type { ApiNewsPage } from '~/types/pages/news';

export interface NewsArticleProps {
  page: ApiNewsPage;
  featured?: boolean;
  expand?: boolean;
  dateFormatter?: Intl.DateTimeFormat;
}

const props = withDefaults(defineProps<NewsArticleProps>(), {
  featured: false,
  expand: false,
  dateFormatter: undefined,
});
const { page, featured, expand } = toRefs(props);

const article = computed(() => page.value.body[0]);
const authorId = computed(() => article.value?.value?.author);
const { author } = useAuthor(authorId);

const formattedDate = computed(() => {
  const raw = page.value.meta?.first_published_at;
  if (!raw || !props.dateFormatter) return null;
  return props.dateFormatter.format(new Date(raw));
});

const contentRef = ref<HTMLElement>();
const { style, isExpanded, toggle } = useLineClamp(contentRef, { expand });

useSchemaOrg([
  defineArticle({
    headline: computed(() => article.value?.value?.heading ? stripHtml(article.value.value.heading) : ''),
    datePublished: computed(() => page.value.meta?.first_published_at ?? ''),
    image: computed(() => article.value?.value?.image?.url ? toCmsMediaPath(article.value.value.image.url) : ''),
    description: computed(() => page.value.meta?.search_description ?? ''),
    inLanguage: 'da',
    author: computed(() => author.value
      ? [{ name: author.value.name ?? '', ...(author.value.value?.title ? { jobTitle: author.value.value.title } : {}) }]
      : []),
  }),
]);
</script>

<template>
  <UCard
    :ui="{ body: 'p-0' }"
    :class="[
      'bg-white transition-shadow duration-300',
      featured ? 'shadow-xl hover:shadow-2xl' : 'shadow-md hover:shadow-lg',
    ]"
  >
    <div class="grid grid-cols-1 md:grid-cols-2">
      <!-- Image (first on mobile, second on desktop) -->
      <div class="order-first md:order-last">
        <Image
          v-if="article?.value?.image"
          :image="article.value.image"
          class="w-full h-full object-cover rounded-t-[calc(var(--ui-radius)*2)] md:rounded-none md:rounded-r-[calc(var(--ui-radius)*2)] min-h-[240px]"
        />
      </div>

      <!-- Content -->
      <div class="p-6 md:p-8 flex flex-col justify-center">
        <p
          v-if="featured"
          class="text-sm font-bold uppercase tracking-widest text-[var(--color-peach)]"
        >
          Nyhed
        </p>

        <h3
          class="mt-2 text-xl md:text-2xl font-bold text-[var(--color-charcoal)]"
        >
          {{ article?.value?.heading ? stripHtml(article.value.heading) : '' }}
        </h3>

        <div class="flex items-center gap-3 mt-4">
          <UAvatar
            v-if="author?.value?.image"
            :src="toCmsMediaPath(author.value.image.url)"
            :alt="author.name ?? ''"
            size="sm"
          />
          <div class="flex flex-col">
            <span
              v-if="author?.name"
              class="text-sm font-medium text-[var(--color-charcoal)]"
            >
              {{ author.name }}
            </span>
            <span
              v-if="formattedDate"
              class="text-xs text-[var(--color-charcoal)]/50"
            >
              {{ formattedDate }}
            </span>
          </div>
        </div>

        <!-- eslint-disable vue/no-v-html cms is safe -->
        <div
          ref="contentRef"
          class="mt-4 text-[var(--color-charcoal)]/70 cms-prose"
          :style="style"
          v-html="article?.value?.content"
        />
        <!-- eslint-enable vue/no-v-html -->

        <div class="mt-6">
          <UButton
            v-if="featured"
            :to="{ name: 'nyheder' }"
            label="Læs mere"
            variant="ghost"
            color="primary"
            trailing-icon="i-heroicons-arrow-right"
            class="rounded-full -ml-2"
          />
          <UButton
            v-else
            :label="isExpanded ? 'Læs mindre' : 'Læs mere'"
            variant="ghost"
            color="primary"
            :trailing-icon="isExpanded ? 'i-heroicons-chevron-up' : 'i-heroicons-chevron-down'"
            class="rounded-full -ml-2"
            @click="toggle()"
          />
        </div>
      </div>
    </div>
  </UCard>
</template>
